import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import {
  Activity,
  Users,
  Play,
  Download,
  Share,
  Eye,
  Globe,
  Smartphone,
  Monitor,
  Clock,
  TrendingUp,
  Wifi,
  MapPin
} from 'lucide-react';
import { useMediaAnalytics, useAnalyticsTracker } from '../../hooks/useMediaAnalytics';
import { AnalyticsDashboard, AnalyticsUpdateEvent } from '../../types/mediaRecord';

interface RecordAnalyticsProps {
  mediaId: string;
  className?: string;
}

const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const formatDuration = (ms: number): string => {
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

interface MetricCardProps {
  icon: React.ComponentType<any>;
  label: string;
  value: number | string;
  change?: number;
  color: string;
  format?: 'number' | 'percentage' | 'duration';
}

const MetricCard: React.FC<MetricCardProps> = ({ 
  icon: Icon, 
  label, 
  value, 
  change, 
  color, 
  format = 'number' 
}) => {
  const formatValue = (val: number | string) => {
    if (typeof val === 'string') return val;
    if (format === 'percentage') return `${Math.round(val * 100)}%`;
    if (format === 'duration') return formatDuration(val);
    return formatNumber(val);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2 }}
      className="cosmic-glass rounded-lg p-4"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${color} bg-opacity-20`}>
            <Icon className={`w-5 h-5 ${color.replace('bg-', 'text-')}`} />
          </div>
          <div>
            <p className="text-sm text-gray-400">{label}</p>
            <p className="text-2xl font-bold text-white">{formatValue(value)}</p>
          </div>
        </div>
        {change !== undefined && (
          <div className={`flex items-center gap-1 text-sm ${
            change >= 0 ? 'text-green-400' : 'text-red-400'
          }`}>
            <TrendingUp className={`w-4 h-4 ${change < 0 ? 'rotate-180' : ''}`} />
            {Math.abs(change).toFixed(1)}%
          </div>
        )}
      </div>
    </motion.div>
  );
};

interface ChartContainerProps {
  title: string;
  children: React.ReactNode;
  className?: string;
}

const ChartContainer: React.FC<ChartContainerProps> = ({ title, children, className }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className={`cosmic-glass rounded-lg p-6 ${className}`}
  >
    <h3 className="text-lg font-semibold text-white mb-4">{title}</h3>
    {children}
  </motion.div>
);

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="cosmic-glass rounded-lg p-3 border border-white/20">
        <p className="text-white font-medium">{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {formatNumber(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export const RecordAnalytics: React.FC<RecordAnalyticsProps> = ({ mediaId, className }) => {
  const [timeRange, setTimeRange] = useState('7d');
  const {
    data: analytics,
    isLoading,
    error,
    concurrentViewers,
    realTimeEvents
  } = useMediaAnalytics(mediaId, {
    timeRange,
    includeRealtime: true,
    includeGeographic: true,
    includeEngagement: true,
    includeDevices: true,
    enableWebSocket: true
  });

  const {
    trackView,
    trackPlay,
    trackPause,
    trackShare,
    sessionId
  } = useAnalyticsTracker(mediaId);

  // Track page view on mount
  useEffect(() => {
    trackView();
  }, [trackView]);

  const timeRangeOptions = [
    { value: '1d', label: 'Last 24 hours' },
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' }
  ];

  const deviceColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];
  const eventColors = {
    view: '#3B82F6',
    play: '#10B981',
    pause: '#F59E0B',
    complete: '#EF4444',
    download: '#8B5CF6',
    share: '#EC4899'
  };

  if (isLoading) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="cosmic-glass rounded-lg p-4 animate-pulse">
              <div className="h-4 bg-white/10 rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-white/10 rounded w-1/2"></div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="cosmic-glass rounded-lg p-6 animate-pulse">
              <div className="h-6 bg-white/10 rounded w-1/3 mb-4"></div>
              <div className="h-64 bg-white/10 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className={`cosmic-glass rounded-lg p-6 ${className}`}>
        <div className="text-center text-red-400">
          <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium mb-2">Failed to load analytics</p>
          <p className="text-sm opacity-75">{error?.message || 'Unknown error occurred'}</p>
        </div>
      </div>
    );
  }

  const { engagement, geographic, devices, timeseries } = analytics;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`space-y-6 ${className}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Analytics Dashboard</h2>
        <div className="flex items-center gap-4">
          {/* Real-time indicator */}
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-300">Live</span>
            {concurrentViewers > 0 && (
              <span className="text-sm text-green-400 font-medium">
                {concurrentViewers} watching
              </span>
            )}
          </div>
          
          {/* Time range selector */}
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-blue-400 transition-colors"
          >
            {timeRangeOptions.map(option => (
              <option key={option.value} value={option.value} className="bg-gray-800">
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      {engagement && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            icon={Eye}
            label="Total Views"
            value={engagement.total_views}
            color="bg-blue-500"
          />
          <MetricCard
            icon={Play}
            label="Total Plays"
            value={engagement.total_plays}
            color="bg-green-500"
          />
          <MetricCard
            icon={Users}
            label="Unique Viewers"
            value={engagement.unique_viewers}
            color="bg-purple-500"
          />
          <MetricCard
            icon={Activity}
            label="Completion Rate"
            value={engagement.completion_rate}
            format="percentage"
            color="bg-orange-500"
          />
        </div>
      )}

      {/* Secondary Metrics */}
      {engagement && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            icon={Download}
            label="Downloads"
            value={engagement.total_downloads}
            color="bg-indigo-500"
          />
          <MetricCard
            icon={Share}
            label="Shares"
            value={engagement.total_shares}
            color="bg-pink-500"
          />
          <MetricCard
            icon={Clock}
            label="Avg Watch Time"
            value={engagement.avg_watch_time_ms || 0}
            format="duration"
            color="bg-cyan-500"
          />
          <MetricCard
            icon={Activity}
            label="Play Rate"
            value={engagement.play_rate}
            format="percentage"
            color="bg-teal-500"
          />
        </div>
      )}

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Engagement Timeline */}
        {timeseries && timeseries.length > 0 && (
          <ChartContainer title="Engagement Over Time">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={timeseries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="time_bucket" 
                  stroke="#9CA3AF"
                  fontSize={12}
                />
                <YAxis stroke="#9CA3AF" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="event_count" 
                  stroke="#3B82F6" 
                  strokeWidth={2}
                  name="Events"
                />
                <Line 
                  type="monotone" 
                  dataKey="unique_users" 
                  stroke="#10B981" 
                  strokeWidth={2}
                  name="Unique Users"
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}

        {/* Geographic Distribution */}
        {geographic && geographic.length > 0 && (
          <ChartContainer title="Geographic Distribution">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={geographic.slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="country" 
                  stroke="#9CA3AF"
                  fontSize={12}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis stroke="#9CA3AF" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="view_count" fill="#3B82F6" name="Views" />
                <Bar dataKey="unique_viewers" fill="#10B981" name="Unique Viewers" />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}

        {/* Device Analytics */}
        {devices && devices.length > 0 && (
          <ChartContainer title="Device Breakdown">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={devices}
                  dataKey="event_count"
                  nameKey="device_type"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ device_type, percent }) => 
                    `${device_type || 'Unknown'} ${(percent * 100).toFixed(0)}%`
                  }
                >
                  {devices.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={deviceColors[index % deviceColors.length]} 
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}

        {/* Real-time Events */}
        <ChartContainer title="Recent Activity">
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {realTimeEvents.length > 0 ? (
              realTimeEvents.map((event, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center gap-3 p-3 bg-white/5 rounded-lg"
                >
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: eventColors[event.data.event_type] || '#6B7280' }}
                  ></div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium capitalize">
                        {event.data.event_type}
                      </span>
                      {event.data.user_id && (
                        <span className="text-xs text-gray-400">
                          User: {event.data.user_id.substring(0, 8)}...
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400">
                      Session: {event.data.session_id.substring(0, 8)}...
                    </div>
                  </div>
                  <div className="text-xs text-gray-400">
                    {new Date(event.timestamp).toLocaleTimeString()}
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center text-gray-400 py-8">
                <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No recent activity</p>
              </div>
            )}
          </div>
        </ChartContainer>
      </div>

      {/* Quick Actions */}
      <div className="cosmic-glass rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => trackPlay()}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            <Play className="w-4 h-4" />
            Track Play Event
          </button>
          <button
            onClick={() => trackPause()}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors"
          >
            <Activity className="w-4 h-4" />
            Track Pause Event
          </button>
          <button
            onClick={() => trackShare('manual')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <Share className="w-4 h-4" />
            Track Share Event
          </button>
        </div>
        {sessionId && (
          <p className="text-xs text-gray-400 mt-3">
            Current session: {sessionId}
          </p>
        )}
      </div>
    </motion.div>
  );
};

export default RecordAnalytics;